/**
 * @module composables/useHistory
 *
 * Singleton state for the "Play History" feature — the list of episodes the
 * user has played, most recently played first. Mirrors the app's other list
 * composables (useLatest / usePlaylist): one shared reactive instance so the
 * view and any trigger points share state.
 *
 * Recording a play is fire-and-forget (best-effort) so it never interferes with
 * playback; the player calls `recordPlay(episodeId)` when an episode starts.
 */
import { ref } from "vue";
import { api } from "../api/config.js";

let historyInstance = null;

export function useHistory() {
  if (historyInstance) return historyInstance;

  /** The user's play-history episodes (most recent first). */
  const history = ref([]);

  /** Whether a history fetch is in progress. */
  const loading = ref(false);

  /**
   * Fetch the play history from the server into `history`.
   */
  const getHistory = async () => {
    loading.value = true;
    try {
      const { data } = await api.get("/history");
      if (data?.success) history.value = data.history || [];
    } catch (error) {
      console.error("Failed to load play history:", error);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Record a play of an episode. Best-effort and fire-and-forget — errors are
   * swallowed so playback is never affected. Only real integer episode ids
   * are sent (defensive guard against any non-numeric id).
   *
   * @param {number|string} episodeId
   */
  const recordPlay = (episodeId) => {
    const id = Number(episodeId);
    if (!Number.isInteger(id) || id <= 0) return;
    api.post(`/history/${id}`).catch(() => {
      /* non-fatal */
    });
  };

  /**
   * Add listened (content) seconds to an episode's running total. Best-effort,
   * fire-and-forget, integer-guarded — the player flushes small increments
   * during playback.
   *
   * @param {number|string} episodeId
   * @param {number} seconds - whole seconds to add (>0)
   */
  const recordListened = (episodeId, seconds) => {
    const id = Number(episodeId);
    const secs = Math.floor(Number(seconds));
    if (!Number.isInteger(id) || id <= 0 || !(secs > 0)) return;
    api.post(`/history/${id}/listened`, { seconds: secs }).catch(() => {
      /* non-fatal */
    });
  };

  /**
   * Remove a single episode from the history (optimistic local update).
   * @param {Object} episode - episode object with an `id`
   */
  const removeFromHistory = async (episode) => {
    const id = episode?.id;
    if (id == null) return;
    try {
      await api.delete(`/history/${id}`);
      history.value = history.value.filter((e) => e.id !== id);
    } catch (error) {
      console.error("Failed to remove from history:", error);
    }
  };

  /**
   * Clear the entire play history.
   */
  const clearHistory = async () => {
    try {
      await api.delete("/history");
      history.value = [];
    } catch (error) {
      console.error("Failed to clear history:", error);
    }
  };

  historyInstance = {
    history,
    loading,
    getHistory,
    recordPlay,
    recordListened,
    removeFromHistory,
    clearHistory,
  };
  return historyInstance;
}
